import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, ChevronDown, ChevronUp, Send, Users, Linkedin, Facebook, Instagram, MessageCircle, Phone, Mail, Plus, Bot, Mic } from 'lucide-react';
import { publicMarketingNavClick } from 'views/public-views/publicNav';
import { SUPPORT_HELP_PUBLIC_PATH } from 'configs/AppConfig';
import { fetchPlanLimitsFromAPI } from 'services/LimitsService';
import { CALCULATOR_STATIC_PLAN_LIMITS, PLAN_API_KEYS } from 'utils/pricingCostCalculator';
import { toSentenceCase } from 'utils/textFormatting';
import PricingCostCalculator from './PricingCostCalculator';
import PublicPageSeo from 'components/seo/PublicPageSeo';

const PLAN_UI_KEYS = ['free', 'payg', 'basic', 'standard', 'advance', 'pro'];

const STATIC_PLAN_META = [
  { key: 'free', name: 'Free', fallbackPrice: '$0/mo', subtitle: 'Testing & Individuals', cta: 'Sign Up' },
  { key: 'payg', name: 'Pay As You Go', fallbackPrice: '$20 min deposit', subtitle: 'Usage-based', cta: 'Sign Up' },
  { key: 'basic', name: 'Basic', fallbackPrice: '$99/mo', subtitle: 'Small teams', cta: 'Sign Up' },
  { key: 'standard', name: 'Standard', fallbackPrice: '$129/mo', subtitle: 'Voice AI focus', cta: 'Sign Up' },
  { key: 'advance', name: 'Advance', fallbackPrice: '$169/mo', subtitle: 'Advanced features for growing businesses', cta: 'Sign Up' },
  { key: 'pro', name: 'Pro', fallbackPrice: '$749/mo', subtitle: 'Enterprise', cta: 'Sign Up', darkCta: true }
];

const SUPPORT_TIER_BY_API_PLAN = {
  Free: 'Email',
  Pay_As_You_Go: 'Priority Email',
  Starter_Chat: 'Priority Email',
  Starter_Voice: 'Priority Email',
  Professional: 'Priority Email',
  Enterprise: 'Dedicated CSM',
};

const readPlanField = (planData, ...keys) => {
  if (!planData || typeof planData !== 'object') return undefined;
  for (const key of keys) {
    const value = planData[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
};

const toMatrixNumber = (raw) => {
  if (raw === undefined || raw === null || raw === '' || raw === 0 || raw === '0') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : raw;
};

const formatUsdRate = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

const hasCallTranscription = (apiPlanKey, planData) => {
  const flag = readPlanField(
    planData,
    'CALL_TRANSCRIPTION',
    'CALL_TRANSCRIPTION_ENABLED',
    'ENABLE_CALL_TRANSCRIPTION'
  );
  if (flag === true || flag === 1 || flag === '1' || flag === 'true') return true;
  if (flag === false || flag === 0 || flag === '0' || flag === 'false') return false;
  return ['Starter_Voice', 'Professional', 'Enterprise'].includes(apiPlanKey);
};

const isUsablePublicPlanLimits = (planLimits) =>
  planLimits &&
  typeof planLimits === 'object' &&
  PLAN_UI_KEYS.some((uiKey) => {
    const apiKey = PLAN_API_KEYS[uiKey];
    return apiKey && planLimits[apiKey] && typeof planLimits[apiKey] === 'object';
  });

const buildPlansFromApi = (planLimits) =>
  STATIC_PLAN_META.map((meta) => {
    const apiKey = PLAN_API_KEYS[meta.key];
    const planData = planLimits?.[apiKey];
    const priceValue = planData?.PRICE;
    if (priceValue === undefined || priceValue === null) {
      return { ...meta, price: meta.fallbackPrice };
    }
    const priceNumber = Number(priceValue);
    if (meta.key === 'free') {
      return { ...meta, price: '$0/mo' };
    }
    if (meta.key === 'payg') {
      const deposit = Number.isFinite(priceNumber) ? priceNumber : 20;
      return { ...meta, price: `$${deposit} min deposit` };
    }
    const monthly = Number.isFinite(priceNumber) ? priceNumber : meta.fallbackPrice.replace(/[^\d]/g, '');
    return { ...meta, price: `$${monthly}/mo` };
  });

const FEATURE_MATRIX = [
  {
    key: 'ai-chat',
    title: 'AI Agent & Chat Limits',
    rows: [
      {
        label: 'Chat AI Agent Creation Limit',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Voice') return 0;
          return toMatrixNumber(
            readPlanField(
              planData,
              'MAX_CHAT_AI_AGENT_CREATION_LIMIT',
              'MAX_CHAT_AGENT_CREATION_LIMIT'
            )
          );
        },
      },
      {
        label: 'Chat AI Agent Update Limit',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Voice') return 0;
          return toMatrixNumber(
            readPlanField(
              planData,
              'MAX_CHAT_AI_AGENT_UPDATE_LIMIT',
              'MAX_CHAT_AGENT_UPDATE_LIMIT',
              'MAX_AGENT_UPDATE_LIMIT'
            )
          );
        },
      },
      {
        label: 'Chat Sessions (Chat AI Agent)',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Pay_As_You_Go') return 'Usage-based';
          if (apiPlanKey === 'Starter_Voice') return 0;
          return toMatrixNumber(
            readPlanField(
              planData,
              'MAX_CHAT_SESSIONS_FOR_CHAT_AI_AGENT_LIMIT',
              'MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT'
            )
          );
        },
      },
      {
        label: 'Playground Chat Sessions',
        resolve: (apiPlanKey, planData) =>
          toMatrixNumber(
            readPlanField(
              planData,
              'MAX_CHAT_SESSIONS_FOR_PLAYGROUND_CHAT_LIMIT',
              'MAX_PLAYGROUND_CHAT_LIMIT'
            )
          ),
      },
      {
        label: 'Max Questions / Playground Session',
        resolve: (apiPlanKey, planData) =>
          toMatrixNumber(
            readPlanField(
              planData,
              'MAX_QUESTIONS_PER_SESSION_FOR_PLAYGROUNDCHAT',
              'MAX_QUESTIONS_FOR_PLAYGROUND_CHAT'
            )
          ),
      },
    ],
  },
  {
    key: 'voice-call',
    title: 'Voice & Call Features',
    rows: [
      {
        label: 'Voice AI Agent Creation Limit',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Chat') return 0;
          return toMatrixNumber(
            readPlanField(
              planData,
              'MAX_VOICE_AI_AGENT_CREATION_LIMIT',
              'MAX_VOICE_AGENT_CREATION_LIMIT',
              'MAX_AGENT_CREATION_LIMIT'
            )
          );
        },
      },
      {
        label: 'Voice AI Agent Update Limit',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Chat') return 0;
          return toMatrixNumber(
            readPlanField(
              planData,
              'MAX_VOICE_AI_AGENT_UPDATE_LIMIT',
              'MAX_VOICE_AGENT_UPDATE_LIMIT',
              'MAX_AGENT_UPDATE_LIMIT'
            )
          );
        },
      },
      {
        label: 'Inbound Call Minutes',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Pay_As_You_Go') return 'Based on deposit';
          if (apiPlanKey === 'Starter_Chat') return 0;
          return toMatrixNumber(readPlanField(planData, 'MAX_INBOUND_CALL_DURATION_MINUTES'));
        },
      },
      {
        label: 'Outbound Call Minutes',
        resolve: (apiPlanKey, planData) =>
          toMatrixNumber(readPlanField(planData, 'MAX_OUTBOUND_CALL_DURATION_MINUTES')),
      },
      {
        label: 'Max Concurrent Calls',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Chat') return 0;
          return toMatrixNumber(readPlanField(planData, 'MAX_CONCURRENT_CALLS'));
        },
      },
      {
        label: 'Max Call Duration (minutes)',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey === 'Starter_Chat') return 0;
          const perCall = readPlanField(planData, 'MAX_CALL_DURATION_MINUTES_PER_CALL');
          if (perCall !== undefined) return toMatrixNumber(perCall);
          const seconds = readPlanField(planData, 'MAX_CALL_DURATION_SECONDS');
          if (seconds !== undefined) return toMatrixNumber(Number(seconds) / 60);
          return 0;
        },
      },
      {
        label: 'Call Transcription',
        boolean: true,
        resolve: (apiPlanKey, planData) => hasCallTranscription(apiPlanKey, planData),
      },
    ],
  },
  {
    key: 'platform',
    title: 'Platform & Management',
    rows: [
      {
        label: 'Document Upload Limit',
        resolve: (_apiPlanKey, planData) =>
          toMatrixNumber(readPlanField(planData, 'MAX_DOCUMENTS_UPLOAD_LIMIT')),
      },
      {
        label: 'User Creation Limit',
        resolve: (_apiPlanKey, planData) =>
          toMatrixNumber(readPlanField(planData, 'MAX_USER_CREATION_LIMIT')),
      },
      {
        label: 'Support Tier',
        resolve: (apiPlanKey, planData) =>
          readPlanField(planData, 'SUPPORT_TIER') || SUPPORT_TIER_BY_API_PLAN[apiPlanKey] || '-',
      },
      {
        label: 'Charge Per Chat Session',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey !== 'Pay_As_You_Go') return '-';
          return formatUsdRate(
            readPlanField(planData, 'CHARGE_PER_CHAT_SESSION', 'CHARGE_PER_SESSION', 'CHARGE_PER_MESSAGE')
          );
        },
      },
      {
        label: 'Charge Per Minute',
        resolve: (apiPlanKey, planData) => {
          if (apiPlanKey !== 'Pay_As_You_Go') return '-';
          return formatUsdRate(readPlanField(planData, 'CHARGE_PER_MINUTE'));
        },
      },
    ],
  },
];

const HIDDEN_PUBLIC_DETAIL_KEYS = new Set([
  'PRICE',
  'MAX_CONCURRENT_CALLS',
  'CHARGE_PER_MESSAGE',
  'MIN_CUSTOM_MESSAGES',
  'MAX_CUSTOM_MESSAGES',
  'CHARGE_PER_MINUTE',
  'MIN_CUSTOM_MINUTES',
  'MAX_CUSTOM_MINUTES',
  'CHARGE_PER_FUSION_CALL',
  'CHARGE_PER_FUSION_PLUS_CALL',
]);

const isShownInPlanDetails = (apiPlanKey, key, value) => {
  if (!key || HIDDEN_PUBLIC_DETAIL_KEYS.has(key)) return false;
  if (value === 0 || value === '0' || value === '0 min' || value === null || value === undefined || value === '') {
    return false;
  }
  if (apiPlanKey === 'Pay_As_You_Go' && key === 'MAX_CHAT_SESSIONS_FOR_CHATAGENT_LIMIT') return false;
  if (apiPlanKey === 'Starter_Voice' && String(key).toUpperCase().includes('CHAT')) return false;
  return true;
};

const formatPlanDetailCell = (apiPlanKey, key, value) => {
  if (!isShownInPlanDetails(apiPlanKey, key, value)) return 0;
  if (
    apiPlanKey === 'Pay_As_You_Go' &&
    (key === 'MAX_INBOUND_CALL_DURATION_MINUTES' || key === 'MAX_CHAT_MESSAGES_FOR_CHATAGENT_LIMIT')
  ) {
    return 'Based on deposit';
  }
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value;
  return value;
};

const buildFeatureGroupsFromApi = (planLimits) => {
  const orderedKeys = [];
  const seen = new Set();

  PLAN_UI_KEYS.forEach((uiKey) => {
    const apiPlanKey = PLAN_API_KEYS[uiKey];
    const planData = planLimits?.[apiPlanKey];
    if (!planData || typeof planData !== 'object') return;
    Object.entries(planData).forEach(([key, value]) => {
      if (seen.has(key) || !isShownInPlanDetails(apiPlanKey, key, value)) return;
      seen.add(key);
      orderedKeys.push(key);
    });
  });

  const rows = orderedKeys.map((key) => {
    const values = {};
    let isBoolean = true;
    PLAN_UI_KEYS.forEach((uiKey) => {
      const apiPlanKey = PLAN_API_KEYS[uiKey];
      const raw = planLimits?.[apiPlanKey]?.[key];
      const cell = formatPlanDetailCell(apiPlanKey, key, raw);
      values[uiKey] = cell;
      if (cell !== 0 && typeof cell !== 'boolean') isBoolean = false;
    });
    const anyBoolean = PLAN_UI_KEYS.some((uiKey) => typeof values[uiKey] === 'boolean');
    return {
      label: toSentenceCase(key),
      boolean: anyBoolean && isBoolean,
      values,
    };
  });

  if (!rows.length) return STATIC_FEATURE_GROUPS;

  return [
    {
      key: 'plan-details',
      title: 'Plan details',
      rows,
    },
  ];
};

const PUBLIC_ADDON_LABELS = {
  WhatsAppAiAgent: { name: 'WhatsApp AI Chat Agent', description: 'Add AI-powered WhatsApp chat support to your plan' },
  InstagramAiAgent: { name: 'Instagram AI Agent', description: 'Add AI-powered Instagram messaging support' },
  FacebookAiAgent: { name: 'Facebook AI Agent', description: 'Integrate AI chat support for Facebook Messenger' },
  EmailAiAgent: { name: 'Email AI Agent', description: 'Automate email responses with AI-powered email support' },
  LineAiAgent: { name: 'Line AI Agent', description: 'Integrate AI chat support for Line messaging' },
  SmsAiAgent: { name: 'SMS AI Agent', description: 'Add AI-powered SMS support to your plan' },
  WeChatAiAgent: { name: 'WeChat AI Agent', description: 'Integrate AI chat support for WeChat' },
  WhatsAppVoiceAgent: { name: 'WhatsApp Voice AI Agent', description: 'Add AI-powered WhatsApp voice support to your plan' },
  VoiceCallRecording: { name: 'Voice Call Recording', description: 'Record voice calls for review and quality monitoring' },
  CsmConversations: { name: 'Fusion+ Conversations', description: 'Extra CSM conversations for Fusion+' },
  Starter_Voice_WebChat_Addon: { name: 'Web Chat Add-on', description: 'Add web chat capabilities to your voice plan' },
  WebAiAgent: { name: 'WebAIAgent', description: 'Add AI-powered web chat support to your plan' },
};

const PUBLIC_ADDON_ICONS = {
  SmsAiAgent: { Icon: MessageCircle, tone: 'blue' },
  WhatsAppVoiceAgent: { Icon: Phone, tone: 'green' },
  VoiceCallRecording: { Icon: Mic, tone: 'violet' },
  WebAiAgent: { Icon: Bot, tone: 'violet' },
  Starter_Voice_WebChat_Addon: { Icon: Bot, tone: 'violet' },
  WeChatAiAgent: { Icon: MessageCircle, tone: 'cyan' },
  WhatsAppAiAgent: { Icon: MessageCircle, tone: 'green' },
  EmailAiAgent: { Icon: Mail, tone: 'violet' },
  LineAiAgent: { Icon: Phone, tone: 'blue' },
  FacebookAiAgent: { Icon: Facebook, tone: 'blue' },
  InstagramAiAgent: { Icon: Instagram, tone: 'pink' },
  CsmConversations: { Icon: Plus, tone: 'violet' },
};

const buildPublicAddons = (planLimits) => {
  const apiAddons = planLimits?.Addons;
  const source =
    apiAddons && typeof apiAddons === 'object' && Object.keys(apiAddons).length
      ? apiAddons
      : CALCULATOR_STATIC_PLAN_LIMITS.Addons;

  return Object.entries(source)
    .filter(([, value]) => value && typeof value === 'object')
    .map(([key, data]) => {
      const label = PUBLIC_ADDON_LABELS[key];
      const price = Number(data.PRICE ?? data.price);
      return {
        key,
        name: label?.name || key.replace(/_/g, ' '),
        description: label?.description || '',
        price: Number.isFinite(price) ? price : null,
      };
    });
};

const STATIC_FEATURE_GROUPS = [
  {
    key: 'ai-chat',
    title: 'AI Agent & Chat Limits',
    rows: [
      { label: 'Chat AI Agent Creation Limit', values: { free: 2, payg: 2, basic: 5, standard: 0, advance: 10, pro: 20 } },
      { label: 'Chat AI Agent Update Limit', values: { free: 5, payg: 10, basic: 10, standard: 0, advance: 10, pro: 10 } },
      { label: 'Chat Sessions (Chat AI Agent)', values: { free: 22, payg: 'Usage-based', basic: 8250, standard: 11000, advance: 11000, pro: 27500 } },
      { label: 'Playground Chat Sessions', values: { free: 1, payg: 10, basic: 100, standard: 100, advance: 100, pro: 100 } },
      { label: 'Max Questions / Playground Session', values: { free: 0, payg: 0, basic: 0, standard: 0, advance: 15, pro: 0 } }
    ]
  },
  {
    key: 'voice-call',
    title: 'Voice & Call Features',
    rows: [
      { label: 'Voice AI Agent Creation Limit', values: { free: 2, payg: 2, basic: 0, standard: 5, advance: 10, pro: 20 } },
      { label: 'Voice AI Agent Update Limit', values: { free: 5, payg: 10, basic: 0, standard: 10, advance: 10, pro: 10 } },
      { label: 'Inbound Call Minutes', values: { free: 30, payg: 'Based on deposit', basic: 350, standard: 500, advance: 500, pro: 1000 } },
      { label: 'Outbound Call Minutes', values: { free: 0, payg: 0, basic: 0, standard: 0, advance: 0, pro: 1000 } },
      { label: 'Max Call Duration (minutes)', values: { free: 3, payg: 10, basic: 0, standard: 10, advance: 10, pro: 10 } },
      { label: 'Call Transcription', values: { free: false, payg: false, basic: false, standard: true, advance: true, pro: true }, boolean: true }
    ]
  },
  {
    key: 'platform',
    title: 'Platform & Management',
    rows: [
      { label: 'Document Upload Limit', values: { free: 2, payg: 20, basic: 20, standard: 20, advance: 20, pro: 20 } },
      { label: 'User Creation Limit', values: { free: 10, payg: 10, basic: 10, standard: 10, advance: 10, pro: 10 } },
      { label: 'Support Tier', values: { free: 'Email', payg: 'Priority Email', basic: 'Priority Email', standard: 'Priority Email', advance: 'Priority Email', pro: 'Dedicated CSM' } },
      { label: 'Charge Per Chat Session', values: { free: '-', payg: '$0.01', basic: '-', standard: '-', advance: '-', pro: '-' } },
      { label: 'Charge Per Minute', values: { free: '-', payg: '$0.20', basic: '-', standard: '-', advance: '-', pro: '-' } }
    ]
  }
];

const STATIC_PLANS = STATIC_PLAN_META.map(({ fallbackPrice, ...meta }) => ({
  ...meta,
  price: fallbackPrice,
}));

const renderCellValue = (value, isBoolean) => {
  if (isBoolean) {
    return value ? <Check size={16} className="cell-icon icon-yes" /> : <Minus size={16} className="cell-icon icon-no" />;
  }
  if (value === 0) return <span className="cell-muted">-</span>;
  return value;
};

const getPlanLevel = (planKey) => {
  const hierarchy = {
    free: 0,
    payg: 1,
    basic: 2,
    standard: 3,
    advance: 4,
    pro: 5
  };
  return hierarchy[planKey] ?? 0;
};

const getCompareSuggestion = (planA, planB) => {
  if (!planA || !planB) return null;
  if (planA.key === planB.key) {
    return {
      title: 'Same plan selected',
      summary: `Both sides are ${planA.name}. Pick a second plan to see upgrade guidance.`,
      points: ['Select two different plans to unlock recommendation insights.']
    };
  }

  const planALevel = getPlanLevel(planA.key);
  const planBLevel = getPlanLevel(planB.key);
  const higher = planALevel >= planBLevel ? planA : planB;
  const lower = planALevel >= planBLevel ? planB : planA;

  const points = [];
  if (higher.key === 'standard' || higher.key === 'advance' || higher.key === 'pro') {
    points.push('Includes stronger voice-agent capabilities for production call workflows.');
  }
  if (higher.key === 'advance') {
    points.push('Adds higher chat/voice creation and update limits for growing teams.');
  }
  if (higher.key === 'pro') {
    points.push('Adds outbound calling and much higher voice/chat capacity for scale.');
    points.push('Best fit for enterprise teams needing broader limits and governance readiness.');
  }
  if (higher.key === 'basic' && lower.key === 'free') {
    points.push('Upgrades from trial-level usage to practical team-level chat operations.');
  }
  if (higher.key === 'payg') {
    points.push('Flexible spend model; pay only for actual usage volume.');
  }
  if (points.length === 0) {
    points.push('Higher plan gives broader limits, better reliability, and room to grow.');
  }

  return {
    title: `Recommendation: ${higher.name}`,
    summary: `${higher.name} is a stronger choice than ${lower.name} for scaling usage and reducing operational limits.`,
    points
  };
};

const SALES_PLAN_MAILTO =
  'mailto:contact@voiceracx.ai?subject=Help%20choosing%20a%20plan&body=Please%20help%20me%20choose%20a%20plan%20based%20on%20my%20expected%20call%20and%20chat%20volume.';

const pricingFaqs = [
  {
    id: 'plan-fit',
    question: 'Which plan works best for me?',
    renderAnswer: () => (
      <>
        <p>
          Start with <strong>Pay As You Go</strong> if you are still learning your volume. You pay for real usage, so you can see how many calls and chat sessions you actually run before locking into a monthly bundle.
        </p>
        <p>
          After a few weeks of data, it is usually clear whether Basic, Standard, Advance, or Pro fits—or whether staying on PAYG makes sense. For a tailored recommendation,{' '}
          <a href={SALES_PLAN_MAILTO}>contact our sales team</a>.
        </p>
      </>
    )
  },
  {
    id: 'payg-billing',
    question: 'How does Pay As You Go billing work?',
    renderAnswer: () => (
      <p>
        You start with a minimum deposit. Chat sessions and call minutes are charged at the published per-session and per-minute rates as you use them. When your balance runs low, you add funds—useful when traffic is uneven or still ramping.
      </p>
    )
  },
  {
    id: 'llm-and-extras',
    question: 'Do I need to bring my own LLM or anything else?',
    renderAnswer: () => (
      <p>
        Our pricing is <strong>easy to read</strong>: you <strong>do not need to bring your own LLM</strong>. The AI that powers your agents is included in your plan—you do not add another paid AI service on the side. The only thing you bring yourself is a <strong>Twilio phone number</strong> so the product can make and receive <strong>normal phone calls</strong>. Everything else follows the limits and rates shown for your tier.
      </p>
    )
  },
  {
    id: 'extras-for-calls',
    question: 'What do I need besides a plan to make or receive calls?',
    renderAnswer: () => (
      <>
        <p>
          We take care of the platform, your AI agent, and how calls are handled on our side. For <strong>regular phone calls</strong>, you <strong>bring one Twilio phone number</strong> and connect it to your workspace. Then people can call in to your agent, and outbound calls can show your business number when the phone network allows it.
        </p>
        <p>We help you connect that Twilio number during onboarding—you are not left to figure out complicated phone-company setup on your own.</p>
      </>
    )
  },
  {
    id: 'india-voice',
    question: 'Can I use Voice AI with a phone number in India?',
    renderAnswer: () => (
      <p>
        In India, Voice AI can be used <strong>only</strong> through <strong>WhatsApp</strong> or <strong>social media</strong> agents, and only on a <strong>monthly commitment</strong> plan. <strong>Direct phone-call (telephone) Voice AI is not supported</strong> in India.
      </p>
    )
  },
  {
    id: 'contract',
    question: 'Is there a long-term contract?',
    renderAnswer: () => (
      <p>
        Monthly plans are typically billed month to month. Pay As You Go stays flexible without a multi-year commitment. Enterprise agreements may differ—{' '}
        <a href={SALES_PLAN_MAILTO}>contact our sales team</a> if you need custom terms.
      </p>
    )
  }
];

const VlmPricing = () => {
  const [planLimits, setPlanLimits] = useState(null);
  const [viewMode, setViewMode] = useState(() =>
    window.location.hash === '#cost-calculator' ? 'calculator' : 'all'
  );
  const [comparePlanA, setComparePlanA] = useState('basic');
  const [comparePlanB, setComparePlanB] = useState('standard');
  const [openCompareDropdown, setOpenCompareDropdown] = useState(null);
  const [openFaqId, setOpenFaqId] = useState(null);
  const [footerEmail, setFooterEmail] = useState('');
  const [visibleBlocks, setVisibleBlocks] = useState({
    heading: false,
    matrix: false
  });
  const [hoveredPlanKey, setHoveredPlanKey] = useState(null);
  const [hoveredCompareCol, setHoveredCompareCol] = useState(null);
  const headingRef = useRef(null);
  const matrixRef = useRef(null);
  const compareControlsRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    fetchPlanLimitsFromAPI()
      .then((data) => {
        if (mounted && isUsablePublicPlanLimits(data)) {
          setPlanLimits(data);
        }
      })
      .catch(() => {
        if (mounted) setPlanLimits(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const plans = useMemo(() => {
    if (!isUsablePublicPlanLimits(planLimits)) return STATIC_PLANS;
    return buildPlansFromApi(planLimits);
  }, [planLimits]);

  const featureGroups = useMemo(() => {
    if (!isUsablePublicPlanLimits(planLimits)) return STATIC_FEATURE_GROUPS;
    return buildFeatureGroupsFromApi(planLimits);
  }, [planLimits]);

  const publicAddons = useMemo(() => buildPublicAddons(planLimits), [planLimits]);

  const handleFooterEmailSubmit = (event) => {
    event.preventDefault();
    const email = footerEmail.trim();
    const subject = encodeURIComponent('Pricing Page Contact Request');
    const body = encodeURIComponent(
      email
        ? `Hi VOICERAcx team,\n\nPlease contact me regarding plans.\n\nMy email: ${email}`
        : 'Hi VOICERAcx team,\n\nPlease contact me regarding plans.'
    );
    window.location.href = `mailto:contact@voiceracx.ai?subject=${subject}&body=${body}`;
  };
  const planA = plans.find((p) => p.key === comparePlanA) || plans[0];
  const planB = plans.find((p) => p.key === comparePlanB) || plans[1];
  const compareSuggestion = getCompareSuggestion(planA, planB);

  const columnLeaveUnlessEnteringSamePlan = (planKey) => (e) => {
    const { relatedTarget } = e;
    if (relatedTarget instanceof Element && relatedTarget.closest(`[data-plan-col="${planKey}"]`)) return;
    setHoveredPlanKey(null);
  };

  const compareColLeaveUnlessEnteringSame = (col) => (e) => {
    const { relatedTarget } = e;
    if (relatedTarget instanceof Element && relatedTarget.closest(`[data-compare-col="${col}"]`)) return;
    setHoveredCompareCol(null);
  };

  useEffect(() => {
    setHoveredPlanKey(null);
    setHoveredCompareCol(null);
  }, [viewMode]);

  useEffect(() => {
    const syncViewFromHash = () => {
      setViewMode((prev) => {
        if (window.location.hash === '#cost-calculator') return 'calculator';
        if (prev === 'calculator') return 'all';
        return prev;
      });
    };
    window.addEventListener('hashchange', syncViewFromHash);
    return () => window.removeEventListener('hashchange', syncViewFromHash);
  }, []);

  useEffect(() => {
    if (viewMode === 'calculator') {
      if (window.location.hash !== '#cost-calculator') {
        window.history.replaceState(null, '', '/pricing#cost-calculator');
      }
      return;
    }
    if (window.location.hash === '#cost-calculator') {
      window.history.replaceState(null, '', '/pricing');
    }
  }, [viewMode]);

  const setPricingView = (mode) => {
    setViewMode(mode);
  };

  useEffect(() => {
    setHoveredCompareCol(null);
  }, [comparePlanA, comparePlanB]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const targetKey = entry.target.getAttribute('data-animate-key');
          if (!targetKey) return;
          setVisibleBlocks((prev) => ({ ...prev, [targetKey]: true }));
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -10% 0px' }
    );

    if (headingRef.current) observer.observe(headingRef.current);
    if (matrixRef.current) observer.observe(matrixRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!compareControlsRef.current) return;
      if (!compareControlsRef.current.contains(event.target)) {
        setOpenCompareDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="pricing-page">
      <PublicPageSeo
        title="VEE Lite Pricing - AI Voice and Chat Plans | VOICERAcx.ai"
        description="Compare VEE Lite pricing plans for AI voice and chat agents. Review limits, usage-based options, and choose the right plan for your business."
        canonicalPath="/pricing"
      />
      <div className="bg-system">
        <div className="nebula blue" />
        <div className="nebula purple" />
        <div className="grid-floor-wrapper">
          <div className="grid-floor-container">
            <div className="grid-lines" />
            <div className="grid-intersections" />
          </div>
        </div>
        <div className="particles">
          <div className="ambient-glow p1" />
          <div className="ambient-glow p2" />
          <div className="ambient-glow p3" />
          <div className="ambient-glow p4" />
          <div className="ambient-glow p5" />
          <div className="ambient-glow p6" />
          <div className="ambient-glow p7" />
          <div className="ambient-glow p8" />
          <div className="ambient-glow p9" />
          <div className="ambient-glow p10" />
          <div className="ambient-glow p11" />
          <div className="ambient-glow p12" />
          <div className="ambient-glow p13" />
          <div className="ambient-glow p14" />
          <div className="ambient-glow p15" />
          <div className="ambient-glow p16" />
          <div className="ambient-glow p17" />
          <div className="ambient-glow p18" />
          <div className="ambient-glow p19" />
          <div className="ambient-glow p20" />
          <div className="ambient-glow p21" />
          <div className="ambient-glow p22" />
          <div className="ambient-glow p23" />
          <div className="ambient-glow p24" />
          <div className="ambient-glow p25" />
          <div className="ambient-glow p26" />
          <div className="ambient-glow p27" />
          <div className="ambient-glow p28" />
          <div className="ambient-glow p29" />
          <div className="ambient-glow p30" />
          <div className="ambient-glow p31" />
          <div className="ambient-glow p32" />
          <div className="ambient-glow p33" />
          <div className="ambient-glow p34" />
          <div className="ambient-glow p35" />
          <div className="ambient-glow p36" />
        </div>
      </div>

    <nav className="navbar">
      <div className="nav-container">
        <div className="nav-glass">
          <div className="nav-logo-group">
            <img src="/voiceralogo.png" alt="VOICERAcx.ai logo" className="logo-img" />
            <span className="logo-text">VOICERAcx.ai</span>
          </div>

          <div className="nav-links">
            <a href="/" className="nav-item" onClick={publicMarketingNavClick('/')}>
              Vee Lite
              <span className="nav-underline" />
            </a>
            <Link to="/#roi-calculator" className="nav-item">
              ROI calculation
              <span className="nav-underline" />
            </Link>
            <a
              href="/pricing"
              className="nav-item nav-item-active"
              onClick={publicMarketingNavClick('/pricing')}
            >
              Pricing
              <span className="nav-underline" />
            </a>
            <a
              href={SUPPORT_HELP_PUBLIC_PATH}
              className="nav-item"
              onClick={publicMarketingNavClick(SUPPORT_HELP_PUBLIC_PATH)}
            >
              Help
              <span className="nav-underline" />
            </a>
          </div>

          <div className="nav-actions">
            <Link to="/auth/login" className="btn-text">
              Sign In
            </Link>
            <Link to="/auth/register-1" className="btn-primary">
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </nav>

      <main className="pricing-main">
        <section className="pricing-section">
          <div className="pricing-container">
            <div
              ref={headingRef}
              data-animate-key="heading"
              className={`pricing-heading reveal-up ${visibleBlocks.heading ? 'is-visible' : ''}`}
            >
              <div className="pricing-section-toggle" role="tablist" aria-label="Pricing views">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'calculator'}
                  className={`pricing-section-toggle__btn ${viewMode === 'calculator' ? 'active' : ''}`}
                  onClick={() => setPricingView('calculator')}
                >
                  Cost Calculator
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode !== 'calculator'}
                  className={`pricing-section-toggle__btn ${viewMode !== 'calculator' ? 'active' : ''}`}
                  onClick={() => setPricingView('all')}
                >
                  Pricing
                </button>
              </div>
              {viewMode !== 'calculator' && (
                <>
                  <h1>
                    Premium <span className="gradient-text">VEE Lite plans</span> built to scale
                  </h1>
                  <p>
                    High-clarity comparison across chat, voice, and platform limits. Pick the right plan in minutes.
                  </p>
                </>
              )}
            </div>

            {viewMode !== 'calculator' && (
              <div className="pricing-mode-tabs">
                <button
                  type="button"
                  className={`mode-tab ${viewMode === 'all' ? 'active' : ''}`}
                  onClick={() => setPricingView('all')}
                >
                  All Plans
                </button>
                <button
                  type="button"
                  className={`mode-tab ${viewMode === 'compare' ? 'active' : ''}`}
                  onClick={() => setPricingView('compare')}
                >
                  Compare Plans
                </button>
                <button
                  type="button"
                  className={`mode-tab ${viewMode === 'addons' ? 'active' : ''}`}
                  onClick={() => setPricingView('addons')}
                >
                  Add-ons
                </button>
              </div>
            )}

            {viewMode !== 'calculator' && (
              <div className="voice-rate-banner">
                <div className="voice-rate-banner__main">
                  <div className="voice-rate-banner__icon" aria-hidden="true">
                    <Phone size={18} strokeWidth={2.25} />
                  </div>
                  <div>
                    <span className="voice-rate-banner__kicker">Voice AI</span>
                    <h3>Voice AI Agents</h3>
                    <p>Orchestration in one rate. Features included: STT, TTS, knowledge base, and tools.</p>
                    <div className="voice-rate-banner__pills">
                      <span>Natural conversations</span>
                      <span>Real-time processing</span>
                      <span>Enterprise ready</span>
                    </div>
                  </div>
                </div>
                <div className="voice-rate-banner__price">
                  <span>Starts from</span>
                  <strong>$0.14</strong>
                  <em>per minute after overage</em>
                </div>
              </div>
            )}

            <div className="mode-panels">
              <div
                ref={matrixRef}
                data-animate-key="matrix"
                className={`matrix-scroll reveal-up mode-panel ${viewMode === 'all' ? 'is-active' : 'is-inactive'} ${visibleBlocks.matrix ? 'is-visible' : ''}`}
                aria-hidden={viewMode !== 'all'}
              >
                <div className="pricing-matrix">
                  <div className="matrix-row matrix-header">
                    <div className="matrix-cell matrix-cell-feature matrix-sticky">Features</div>
                    {plans.map((plan) => (
                      <div
                        key={plan.key}
                        role="presentation"
                        data-plan-col={plan.key}
                        className={`matrix-cell matrix-cell-plan ${hoveredPlanKey === plan.key ? 'is-column-highlight' : ''}`}
                        onMouseEnter={() => setHoveredPlanKey(plan.key)}
                        onMouseLeave={columnLeaveUnlessEnteringSamePlan(plan.key)}
                      >
                        <h3>{plan.name}</h3>
                        <p className="plan-subtitle">{plan.subtitle}</p>
                        <p className="plan-price">{plan.price}</p>
                        <Link
                          to="/auth/register-1"
                          className={`plan-cta ${plan.darkCta ? 'cta-dark' : ''}`}
                        >
                          {plan.cta}
                        </Link>
                      </div>
                    ))}
                  </div>

                  {featureGroups.map((group, groupIdx) => (
                    <React.Fragment key={group.key}>
                      {group.rows.map((row, rowIdx) => (
                        <div
                          key={`${group.key}-${row.label}`}
                          className="matrix-row matrix-feature-row row-animated"
                          style={{ animationDelay: `${groupIdx * 80 + rowIdx * 55}ms` }}
                        >
                          <div className="matrix-cell matrix-cell-feature matrix-sticky feature-label">{row.label}</div>
                          {plans.map((plan) => (
                            <div
                              key={`${group.key}-${row.label}-${plan.key}`}
                              role="presentation"
                              data-plan-col={plan.key}
                              className={`matrix-cell ${hoveredPlanKey === plan.key ? 'is-column-highlight' : ''}`}
                              onMouseEnter={() => setHoveredPlanKey(plan.key)}
                              onMouseLeave={columnLeaveUnlessEnteringSamePlan(plan.key)}
                            >
                              {renderCellValue(row.values[plan.key], row.boolean)}
                            </div>
                          ))}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div
                className={`compare-wrap reveal-up mode-panel ${viewMode === 'compare' ? 'is-active' : 'is-inactive'} ${visibleBlocks.matrix ? 'is-visible' : ''}`}
                aria-hidden={viewMode !== 'compare'}
              >
                <div className="compare-controls" ref={compareControlsRef}>
                  <label className="compare-select">
                    <span>Plan A</span>
                    <button
                      type="button"
                      className="compare-dropdown-trigger"
                      onClick={() => setOpenCompareDropdown((prev) => (prev === 'a' ? null : 'a'))}
                    >
                      <span>{planA.name}</span>
                      <ChevronDown size={15} className={`compare-dropdown-icon ${openCompareDropdown === 'a' ? 'is-open' : ''}`} />
                    </button>
                    {openCompareDropdown === 'a' && (
                      <div className="compare-dropdown-menu">
                        {plans.map((p) => (
                          <button
                            key={`a-${p.key}`}
                            type="button"
                            className={`compare-dropdown-item ${p.key === comparePlanA ? 'active' : ''}`}
                            disabled={p.key === comparePlanB}
                            onClick={() => {
                              setComparePlanA(p.key);
                              setOpenCompareDropdown(null);
                            }}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </label>

                  <label className="compare-select">
                    <span>Plan B</span>
                    <button
                      type="button"
                      className="compare-dropdown-trigger"
                      onClick={() => setOpenCompareDropdown((prev) => (prev === 'b' ? null : 'b'))}
                    >
                      <span>{planB.name}</span>
                      <ChevronDown size={15} className={`compare-dropdown-icon ${openCompareDropdown === 'b' ? 'is-open' : ''}`} />
                    </button>
                    {openCompareDropdown === 'b' && (
                      <div className="compare-dropdown-menu">
                        {plans.map((p) => (
                          <button
                            key={`b-${p.key}`}
                            type="button"
                            className={`compare-dropdown-item ${p.key === comparePlanB ? 'active' : ''}`}
                            disabled={p.key === comparePlanA}
                            onClick={() => {
                              setComparePlanB(p.key);
                              setOpenCompareDropdown(null);
                            }}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </label>
                </div>

                {compareSuggestion && (
                  <div className="compare-suggestion-card">
                    <p className="compare-suggestion-title">{compareSuggestion.title}</p>
                    <p className="compare-suggestion-summary">{compareSuggestion.summary}</p>
                    <ul className="compare-suggestion-list">
                      {compareSuggestion.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="matrix-scroll">
                  <div className="pricing-matrix compare-matrix">
                    <div className="matrix-row compare-header-row">
                      <div className="matrix-cell matrix-cell-feature matrix-sticky">Feature</div>
                      <div
                        role="presentation"
                        data-compare-col="a"
                        className={`matrix-cell matrix-cell-plan ${hoveredCompareCol === 'a' ? 'is-column-highlight' : ''}`}
                        onMouseEnter={() => setHoveredCompareCol('a')}
                        onMouseLeave={compareColLeaveUnlessEnteringSame('a')}
                      >
                        <h3>{planA.name}</h3>
                        <p className="plan-subtitle">{planA.subtitle}</p>
                        <p className="plan-price">{planA.price}</p>
                      </div>
                      <div
                        role="presentation"
                        data-compare-col="b"
                        className={`matrix-cell matrix-cell-plan ${hoveredCompareCol === 'b' ? 'is-column-highlight' : ''}`}
                        onMouseEnter={() => setHoveredCompareCol('b')}
                        onMouseLeave={compareColLeaveUnlessEnteringSame('b')}
                      >
                        <h3>{planB.name}</h3>
                        <p className="plan-subtitle">{planB.subtitle}</p>
                        <p className="plan-price">{planB.price}</p>
                      </div>
                    </div>

                    {featureGroups.map((group) => (
                      <React.Fragment key={`compare-${group.key}`}>
                        <div className="matrix-row matrix-group-row compare-group-row">
                          <div className="matrix-cell matrix-cell-feature matrix-sticky compare-group-title">{group.title}</div>
                          <div
                            role="presentation"
                            data-compare-col="a"
                            className={`matrix-cell matrix-group-fill ${hoveredCompareCol === 'a' ? 'is-column-highlight' : ''}`}
                            onMouseEnter={() => setHoveredCompareCol('a')}
                            onMouseLeave={compareColLeaveUnlessEnteringSame('a')}
                          />
                          <div
                            role="presentation"
                            data-compare-col="b"
                            className={`matrix-cell matrix-group-fill ${hoveredCompareCol === 'b' ? 'is-column-highlight' : ''}`}
                            onMouseEnter={() => setHoveredCompareCol('b')}
                            onMouseLeave={compareColLeaveUnlessEnteringSame('b')}
                          />
                        </div>
                        {group.rows.map((row) => (
                          <div key={`compare-${group.key}-${row.label}`} className="matrix-row matrix-feature-row">
                            <div className="matrix-cell matrix-cell-feature matrix-sticky feature-label">{row.label}</div>
                            <div
                              role="presentation"
                              data-compare-col="a"
                              className={`matrix-cell ${hoveredCompareCol === 'a' ? 'is-column-highlight' : ''}`}
                              onMouseEnter={() => setHoveredCompareCol('a')}
                              onMouseLeave={compareColLeaveUnlessEnteringSame('a')}
                            >
                              {renderCellValue(row.values[planA.key], row.boolean)}
                            </div>
                            <div
                              role="presentation"
                              data-compare-col="b"
                              className={`matrix-cell ${hoveredCompareCol === 'b' ? 'is-column-highlight' : ''}`}
                              onMouseEnter={() => setHoveredCompareCol('b')}
                              onMouseLeave={compareColLeaveUnlessEnteringSame('b')}
                            >
                              {renderCellValue(row.values[planB.key], row.boolean)}
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              <div
                className={`addons-wrap reveal-up mode-panel ${viewMode === 'addons' ? 'is-active' : 'is-inactive'}`}
                aria-hidden={viewMode !== 'addons'}
              >
                <div className="addons-grid">
                  {publicAddons.map((addon) => {
                    const icon = PUBLIC_ADDON_ICONS[addon.key] || { Icon: MessageCircle, tone: 'blue' };
                    const AddonIcon = icon.Icon;
                    return (
                      <article key={addon.key} className="addon-card">
                        <div className="addon-card__top">
                          <span className={`addon-card__icon tone-${icon.tone}`} aria-hidden>
                            <AddonIcon size={18} strokeWidth={2.2} />
                          </span>
                          <div>
                            <h3>{addon.name}</h3>
                            {addon.description ? <p>{addon.description}</p> : null}
                          </div>
                        </div>
                        <div className="addon-card__foot">
                          <p className="addon-card__price">
                            {addon.price == null ? '—' : `$${addon.price}/mo`}
                          </p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div
                className={`cost-calculator-wrap reveal-up mode-panel ${viewMode === 'calculator' ? 'is-active' : 'is-inactive'}`}
                aria-hidden={viewMode !== 'calculator'}
              >
                <div className="cost-calculator-panel" aria-label="Pricing cost calculator">
                  <PricingCostCalculator />
                </div>
              </div>
            </div>

            <p className="pricing-footnote">All prices billed monthly. PAYG charges apply based on real usage.</p>
          </div>
        </section>

        <section className="pricing-faq-section" aria-labelledby="pricing-faq-heading">
          <div className="pricing-container">
            <div className="pricing-faq-heading">
              <p className="pricing-label">FAQ</p>
              <h2 id="pricing-faq-heading">Plans, billing, and calls</h2>
              <p className="pricing-faq-lead">Tap a question to open the full answer.</p>
            </div>
            <div className="pricing-faq-shell">
              <ul className="pricing-faq-list">
                {pricingFaqs.map((item) => {
                  const isOpen = openFaqId === item.id;
                  return (
                    <li key={item.id} className={`pricing-faq-item${isOpen ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="pricing-faq-trigger"
                        aria-expanded={isOpen}
                        aria-controls={`pricing-faq-panel-${item.id}`}
                        id={`pricing-faq-trigger-${item.id}`}
                        onClick={() => setOpenFaqId((prev) => (prev === item.id ? null : item.id))}
                      >
                        <span className="pricing-faq-q">{item.question}</span>
                        <span className="pricing-faq-chevron-btn" aria-hidden>
                          {isOpen ? <ChevronUp size={17} strokeWidth={2.25} /> : <ChevronDown size={17} strokeWidth={2.25} />}
                        </span>
                      </button>
                      <div
                        id={`pricing-faq-panel-${item.id}`}
                        role="region"
                        aria-labelledby={`pricing-faq-trigger-${item.id}`}
                        aria-hidden={!isOpen}
                        className={`pricing-faq-panel${isOpen ? ' is-open' : ''}`}
                      >
                        <div className="pricing-faq-panel-inner">
                          <div className="pricing-faq-answer">{item.renderAnswer()}</div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="theme-footer">
        <div className="theme-footer-inner">
          <div className="theme-footer-top">
            <div className="footer-cta-block">
              <h3>Stay in the loop</h3>
              <p>Get product updates, AI insights, and platform improvement notes.</p>
              <form className="footer-email-wrap" onSubmit={handleFooterEmailSubmit}>
                <input
                  type="email"
                  value={footerEmail}
                  onChange={(e) => setFooterEmail(e.target.value)}
                  placeholder="Enter your email"
                  aria-label="Email address"
                  required
                />
                <button type="submit" aria-label="Submit email">
                  <Send size={14} />
                </button>
              </form>
            </div>
          </div>

          <div className="theme-footer-mid">
            <div className="footer-brand-col">
              <div className="footer-brand-head">
                <img src="/voiceralogo.png" alt="VOICERAcx.ai logo" className="footer-brand-logo" />
                <span>VOICERAcx.ai</span>
              </div>
              <p>
                Building practical AI for conversations across voice and chat channels.
              </p>
              <div className="footer-socials">
                <a className="social-instagram" href="https://www.instagram.com/voiceracx.ai/" target="_blank" rel="noreferrer" aria-label="Instagram"><Instagram size={15} /></a>
                <a className="social-facebook" href="https://www.facebook.com/profile.php?id=61579626261121&sk=about" target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={15} /></a>
                <a className="social-linkedin" href="https://www.linkedin.com/company/voiceracx/posts/?feedView=all" target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={15} /></a>
              </div>
            </div>
          </div>

          <div className="theme-footer-bottom">
            <p>© 2026 VOICERAcx.ai. All rights reserved.</p>
            <div className="footer-legal-links">
              <a href="/privacy">
                Privacy Policy
              </a>
              <a href="/terms">
                Terms of Use
              </a>
            </div>
          </div>
        </div>
      </footer>

    <style dangerouslySetInnerHTML={{ __html: `
      :root { --purple:#4F46E5; --blue:#2563EB; --green:#22C55E; --bg:#FFFFFF; --text:#1E293B; --muted:#64748B; --font-main:'Plus Jakarta Sans', sans-serif; }
      .pricing-page { min-height:100vh; background:linear-gradient(180deg, #FFFFFF 0%, #FCFFFD 46%, #F7FCF9 72%, #FFFFFF 100%); color:var(--text); font-family:var(--font-main); position:relative; overflow-x:hidden; scroll-behavior:smooth; }
      .bg-system { position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
      .nebula { position:absolute; border-radius:9999px; filter:blur(140px); opacity:0.045; animation:pulse 8s infinite ease-in-out; }
      .nebula.blue { bottom:-28%; right:-20%; width:100vw; height:70vh; background-color:rgba(34,197,94,.08); }
      .nebula.purple { top:-22%; left:-18%; width:80vw; height:60vh; background-color:rgba(167,243,208,.1); animation-duration:10s; }
      .grid-floor-wrapper { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; opacity:1; }
      .grid-floor-container { position:absolute; bottom:0; width:100%; height:100vh; transform-origin:bottom; transform:rotateX(65deg); }
      .grid-lines { position:absolute; inset:0; background-image:radial-gradient(circle, rgba(30,41,59,0.22) 0.6px, transparent 0.6px); background-size:34px 34px; opacity:0.04; }
      .grid-intersections { display:none; }
      .particles { position:absolute; inset:0; pointer-events:none; }
      .ambient-glow { position:absolute; width:14px; height:14px; border-radius:999px; opacity:0.06; filter:blur(.4px); }
      .p1 { top:12%; left:7%; background:var(--blue); }
      .p2 { top:22%; right:6%; background:var(--purple); }
      .p3 { bottom:16%; left:4%; background:var(--blue); }
      .p4 { top:44%; left:3%; background:var(--purple); }
      .p5 { top:7%; right:24%; background:var(--blue); }
      .p6 { top:56%; right:11%; background:var(--purple); }
      .p7 { bottom:9%; right:17%; background:var(--blue); }
      .p8 { top:34%; left:14%; background:var(--purple); }
      .p9 { bottom:32%; right:7%; background:var(--blue); }
      .p10 { top:69%; left:21%; background:var(--purple); }
      .p11 { top:17%; left:38%; background:var(--blue); }
      .p12 { bottom:40%; left:6%; background:var(--purple); }
      .p13 { top:28%; left:52%; background:var(--blue); }
      .p14 { top:84%; left:11%; background:var(--purple); }
      .p15 { bottom:6%; right:34%; background:var(--blue); }
      .p16 { top:48%; right:28%; background:var(--purple); }
      .p17 { bottom:22%; left:33%; background:var(--blue); }
      .p18 { top:61%; left:44%; background:var(--purple); }
      .p19 { bottom:46%; right:21%; background:var(--blue); }
      .p20 { top:25%; left:61%; background:var(--purple); }
      .p21 { top:77%; right:41%; background:var(--blue); }
      .p22 { top:11%; left:27%; background:var(--purple); }
      .p23 { bottom:11%; left:51%; background:var(--blue); }
      .p24 { top:51%; right:3%; background:var(--purple); }
      .p25 { top:5%; left:49%; background:var(--blue); }
      .p26 { top:39%; left:8%; background:var(--purple); }
      .p27 { top:72%; left:9%; background:var(--blue); }
      .p28 { top:91%; left:27%; background:var(--purple); }
      .p29 { top:37%; left:71%; background:var(--blue); }
      .p30 { top:57%; left:57%; background:var(--purple); }
      .p31 { bottom:27%; right:44%; background:var(--blue); }
      .p32 { top:14%; right:39%; background:var(--purple); }
      .p33 { bottom:54%; left:17%; background:var(--blue); }
      .p34 { top:81%; left:37%; background:var(--purple); }
      .p35 { top:31%; right:37%; background:var(--blue); }
      .p36 { bottom:17%; right:8%; background:var(--purple); }
      .navbar { position:fixed; top:0; left:0; right:0; z-index:50; padding:0; background:transparent; }
      .nav-container { max-width:none; margin:0; padding:0; width:100%; }
      .nav-glass { width:100%; height:64px; display:flex; align-items:center; justify-content:space-between; background:rgba(245,253,248,.84); border:1px solid rgba(187,247,208,.72); border-radius:0; padding:0 18px; backdrop-filter:blur(14px) saturate(140%); -webkit-backdrop-filter:blur(14px) saturate(140%); box-shadow:0 8px 20px rgba(34,197,94,.10), inset 0 1px 0 rgba(255,255,255,.8); }
      .nav-logo-group { display:flex; align-items:center; gap:.5rem; }
      .logo-img { width:2rem; height:2rem; object-fit:contain; }
      .logo-text { font-weight:bold; font-size:1.25rem; background:linear-gradient(to right, #1E293B, #334155); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
      .nav-links { display:none; gap:1.75rem; }
      .nav-item { color:#5f7d69; text-decoration:none; font-size:.875rem; font-weight:500; position:relative; transition:color .3s; }
      .nav-item:hover, .nav-item-active { color:var(--text); }
      .nav-underline { position:absolute; bottom:-.25rem; left:0; width:0; height:1px; background:#22c55e; transition:width .3s; }
      .nav-item:hover .nav-underline, .nav-item-active .nav-underline { width:100%; }
      .nav-actions { display:flex; align-items:center; gap:.8rem; }
      .btn-text { background:transparent; border:none; color:#5f7d69; font-weight:500; text-decoration:none; display:none; transition:color .25s ease, transform .25s ease; }
      .btn-text:hover { color:var(--text); transform:translateY(-1px); }
      .btn-primary { background:linear-gradient(90deg, var(--blue) 0%, var(--purple) 100%); color:white; border:none; padding:.5rem 1.25rem; border-radius:.75rem; font-weight:600; text-decoration:none; transition:filter .25s ease, transform .25s ease, box-shadow .25s ease; box-shadow:0 6px 16px rgba(37,99,235,.12); }
      .btn-primary:hover { filter:brightness(1.05); transform:translateY(-1px); box-shadow:0 12px 24px rgba(79,70,229,.12); }
      .btn-primary:active { transform:translateY(0); box-shadow:0 6px 14px rgba(37,99,235,.12); }
      .pricing-main { padding:5.8rem 1.25rem 3rem; }
      .pricing-section { position:relative; z-index:10; scroll-margin-top:5rem; }
      .pricing-container { max-width:82rem; margin:0 auto; }
      .pricing-heading { text-align:center; max-width:720px; margin:0 auto 1.8rem; }
      .pricing-section-toggle { margin:0 auto 14px; max-width:340px; display:grid; grid-template-columns:1fr 1fr; gap:6px; background:rgba(255,255,255,.74); border:1px solid rgba(226,232,240,.9); border-radius:999px; padding:5px; box-shadow:0 8px 18px rgba(15,23,42,.05); }
      .pricing-section-toggle__btn { border:none; border-radius:999px; padding:8px 14px; font-family:ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; font-size:.68rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#64748b; background:transparent; cursor:pointer; transition:all .2s ease; }
      .pricing-section-toggle__btn.active { color:var(--blue); background:#eef2ff; box-shadow:0 4px 10px rgba(79,70,229,.12); }
      .pricing-label { margin:0 0 8px; font-family:ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; color:var(--blue); letter-spacing:.08em; font-size:.68rem; }
      .pricing-heading h1 { margin:0 0 10px; font-size:clamp(1.8rem, 3.2vw, 2.8rem); color:var(--text); letter-spacing:-.02em; }
      .pricing-heading p { margin:0; color:var(--muted); font-size:1rem; line-height:1.65; }
      .voice-rate-banner { position:relative; overflow:hidden; display:flex; align-items:center; justify-content:space-between; gap:16px; margin:0 0 18px; padding:12px 18px 12px 14px; border-radius:18px; border:1px solid rgba(199,210,254,.9); background:radial-gradient(120px 80px at 8% 20%, rgba(255,255,255,.9), transparent 70%), linear-gradient(100deg, #f4f7ff 0%, #eef2ff 42%, #f7f5ff 100%); box-shadow:0 8px 24px rgba(99,102,241,.06); text-align:left; }
      .voice-rate-banner::after { content:''; position:absolute; right:150px; top:8px; width:180px; height:calc(100% - 16px); background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 180 70' fill='none'%3E%3Cpath d='M0 46c18-16 28-16 46 0s28 16 46 0 28-16 46 0 28 16 42 6' stroke='%23c7d2fe' stroke-width='1.4'/%3E%3Cpath d='M8 36c16-12 26-12 42 0s26 12 42 0 26-12 42 0 22 10 38 4' stroke='%23a5b4fc' stroke-width='1.2' opacity='.7'/%3E%3Cpath d='M16 26c14-8 22-8 36 0s22 8 36 0 22-8 36 0 18 6 32 2' stroke='%23ddd6fe' stroke-width='1.2'/%3E%3C/svg%3E") center / contain no-repeat; pointer-events:none; opacity:.85; }
      .voice-rate-banner__main { position:relative; z-index:1; display:flex; align-items:flex-start; gap:12px; min-width:0; }
      .voice-rate-banner__icon { width:40px; height:40px; flex:0 0 40px; border-radius:12px; display:grid; place-items:center; color:#4f46e5; background:#fff; border:1px solid rgba(199,210,254,.95); box-shadow:0 4px 12px rgba(79,70,229,.08); }
      .voice-rate-banner__kicker { display:inline-flex; align-items:center; margin-bottom:2px; padding:1px 8px; border-radius:999px; background:#eef2ff; color:#4f46e5; font-size:10px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
      .voice-rate-banner h3 { margin:0; color:#0f172a; font-size:20px; line-height:1.15; font-weight:800; letter-spacing:-0.03em; }
      .voice-rate-banner p { margin:2px 0 0; color:#64748b; font-size:13px; line-height:1.35; }
      .voice-rate-banner__pills { display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
      .voice-rate-banner__pills span { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:999px; background:rgba(255,255,255,.82); border:1px solid rgba(226,232,240,.9); color:#475569; font-size:11px; font-weight:650; }
      .voice-rate-banner__pills span::before { content:'✓'; width:14px; height:14px; border-radius:999px; display:inline-grid; place-items:center; background:#eef2ff; color:#4f46e5; font-size:9px; font-weight:800; }
      .voice-rate-banner__price { position:relative; z-index:1; flex:0 0 auto; text-align:right; padding-left:12px; }
      .voice-rate-banner__price span { display:block; color:#64748b; font-size:12px; font-weight:600; }
      .voice-rate-banner__price strong { display:block; margin-top:0; color:#2563eb; font-size:32px; line-height:1; font-weight:800; letter-spacing:-0.04em; }
      .voice-rate-banner__price em { display:block; margin-top:2px; color:#64748b; font-size:12px; font-style:normal; font-weight:500; }
      .pricing-mode-tabs { margin:0 auto 14px; max-width:520px; display:grid; grid-template-columns:1fr 1fr 1fr; gap:8px; background:rgba(255,255,255,.74); border:1px solid rgba(226,232,240,.9); border-radius:999px; padding:6px; box-shadow:0 8px 18px rgba(15,23,42,.05); }
      .mode-tab { border:none; border-radius:999px; padding:8px 12px; font-size:.82rem; font-weight:700; color:#64748b; background:transparent; cursor:pointer; transition:all .2s ease; }
      .mode-tab.active { color:#1e293b; background:#eef2ff; box-shadow:0 4px 10px rgba(79,70,229,.12); }
      .gradient-text { background:linear-gradient(to right, var(--purple), var(--blue)); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
      .reveal-up { opacity:0; transform:translateY(22px) scale(.985); filter:blur(2px); transition:opacity .72s ease, transform .72s ease, filter .72s ease; }
      .reveal-up.is-visible { opacity:1; transform:translateY(0) scale(1); filter:blur(0); }
      .mode-panels { position:relative; display:grid; gap:0; }
      .mode-panel { overflow:hidden; transform-origin:top; transition:opacity .34s ease, transform .34s ease, max-height .42s ease, margin .34s ease, filter .34s ease; }
      .mode-panel.is-active { opacity:1; transform:translateY(0) scale(1); max-height:5000px; margin-top:0; pointer-events:auto; filter:blur(0); }
      .mode-panel.is-inactive { opacity:0; transform:translateY(10px) scale(.99); max-height:0; margin-top:-2px; pointer-events:none; filter:blur(1px); }
      .addons-wrap { margin-top:4px; }
      .addons-grid { display:grid; grid-template-columns:repeat(3, minmax(0, 1fr)); gap:16px; }
      .addon-card { background:#fff; border:1px solid rgba(226,232,240,.9); border-radius:1.15rem; padding:16px 16px 14px; box-shadow:0 10px 24px rgba(15,23,42,.05); display:flex; flex-direction:column; gap:16px; min-height:148px; }
      .addon-card__top { display:flex; align-items:flex-start; gap:12px; }
      .addon-card__icon { width:40px; height:40px; border-radius:12px; display:inline-flex; align-items:center; justify-content:center; flex-shrink:0; }
      .addon-card__icon.tone-blue { background:#e8f1ff; color:#3b82f6; }
      .addon-card__icon.tone-green { background:#e8fbf1; color:#22c55e; }
      .addon-card__icon.tone-violet { background:#f3e8ff; color:#8b5cf6; }
      .addon-card__icon.tone-cyan { background:#e7f8fb; color:#06b6d4; }
      .addon-card__icon.tone-pink { background:#fde8f3; color:#ec4899; }
      .addon-card h3 { margin:0 0 2px; font-size:.98rem; font-weight:700; color:#0f172a; line-height:1.3; }
      .addon-card__top p { margin:0; color:#64748b; font-size:.8rem; line-height:1.4; }
      .addon-card__foot { margin-top:auto; display:flex; align-items:center; justify-content:space-between; gap:10px; }
      .addon-card__price { margin:0; font-size:1.35rem; font-weight:800; color:#2563eb; letter-spacing:-.02em; }
      @media (max-width:900px) { .addons-grid { grid-template-columns:1fr 1fr; } }
      @media (max-width:640px) { .pricing-mode-tabs { max-width:100%; } .addons-grid { grid-template-columns:1fr; } }
      .matrix-scroll { overflow-x:auto; padding-bottom:4px; width:100%; }
      .pricing-matrix { width:100%; min-width:1040px; border-radius:2.5rem; overflow:hidden; background:#ffffff; border:1px solid rgba(226,232,240,.95); box-shadow:0 24px 40px rgba(15,23,42,.08); animation:matrixFloatLite 8s ease-in-out infinite; will-change:transform, box-shadow; }
      .matrix-row { display:grid; grid-template-columns:minmax(260px, 1.38fr) repeat(6, minmax(156px, 1fr)); }
      .matrix-cell { padding:16px 14px; border-right:1px solid rgba(226,232,240,.75); border-bottom:1px solid rgba(226,232,240,.75); background:#ffffff; color:#0f172a; font-size:.92rem; line-height:1.42; display:flex; align-items:center; justify-content:center; text-align:center; }
      .matrix-sticky { position:sticky; left:0; z-index:5; box-shadow:6px 0 14px rgba(15,23,42,.04); }
      .matrix-cell-feature { justify-content:flex-start; text-align:left; background:#f8fafc; font-weight:600; color:#334155; }
      .matrix-header .matrix-cell { min-height:184px; align-items:stretch; padding-top:18px; padding-bottom:18px; }
      .matrix-cell-plan { flex-direction:column; align-items:flex-start; text-align:left; gap:8px; position:relative; height:100%; }
      .matrix-cell-plan h3 { margin:0; font-size:1rem; font-weight:700; color:#0f172a; }
      .plan-subtitle { margin:0; color:#64748b; font-size:.82rem; line-height:1.35; min-height:2.4em; }
      .plan-price { margin:2px 0 10px; font-size:1.24rem; font-weight:700; color:#0f172a; line-height:1.22; letter-spacing:.005em; }
      .plan-badge { position:absolute; top:8px; right:8px; font-size:.64rem; font-weight:700; color:#3730a3; background:rgba(224,231,255,.95); border:1px solid rgba(165,180,252,.95); border-radius:999px; padding:4px 7px; }
      .plan-cta { margin-top:auto; border:none; border-radius:.7rem; padding:.54rem .78rem; background:linear-gradient(90deg, #2563EB 0%, #4F46E5 100%); color:#fff; font-size:.79rem; font-weight:600; letter-spacing:.01em; line-height:1.2; text-decoration:none; display:inline-flex; align-items:center; justify-content:center; transition:transform .2s ease, filter .2s ease, box-shadow .2s ease; box-shadow:0 6px 14px rgba(79,70,229,.22); }
      .plan-cta:hover { transform:translateY(-1px); filter:brightness(1.06); box-shadow:0 10px 20px rgba(79,70,229,.30); }
      .plan-cta:active { transform:translateY(0); filter:brightness(1); box-shadow:0 6px 14px rgba(79,70,229,.22); }
      .cta-dark { background:linear-gradient(90deg, #2563EB 0%, #4F46E5 100%); color:#fff; }
      .matrix-cell.is-column-highlight { background:linear-gradient(180deg, rgba(238,242,255,.82) 0%, rgba(255,255,255,1) 100%); transition:background .2s ease; }
      .matrix-group-row .matrix-cell { background:#f8fafc; min-height:52px; }
      .matrix-group-row .matrix-cell.is-column-highlight { background:linear-gradient(180deg, rgba(238,242,255,.88) 0%, rgba(248,250,252,.98) 100%); }
      .matrix-group-toggle { border:none; width:100%; min-height:52px; display:flex; align-items:center; justify-content:space-between; padding:12px 14px; background:#f8fafc; color:#334155; font-weight:700; cursor:pointer; }
      .matrix-group-fill { background:#f8fafc; }
      .matrix-group-body { overflow:hidden; transition:max-height .42s ease, opacity .3s ease; }
      .matrix-group-body.is-open { max-height:calc(var(--group-rows) * 56px + 8px); opacity:1; }
      .matrix-group-body.is-closed { max-height:0; opacity:.25; }
      .matrix-feature-row .matrix-cell { min-height:54px; }
      .row-animated { animation:rowEnter .45s ease both; }
      .feature-label { font-weight:600; line-height:1.35; }
      .cell-icon { flex:0 0 auto; }
      .icon-yes { color:#16a34a; }
      .icon-no { color:#94a3b8; }
      .cell-muted { color:#94a3b8; }
      .compare-wrap { display:grid; gap:12px; }
      .cost-calculator-wrap { display:grid; margin-top:4px; }
      .cost-calculator-panel { min-height:0; border-radius:0; background:transparent; border:none; box-shadow:none; }
      .compare-controls { display:flex; gap:10px; justify-content:flex-end; flex-wrap:wrap; }
      .compare-select { display:flex; flex-direction:column; gap:6px; position:relative; min-width:170px; }
      .compare-select span { color:#64748b; font-size:.76rem; font-weight:700; }
      .compare-dropdown-trigger { border:1px solid rgba(203,213,225,.95); border-radius:.65rem; padding:.5rem .62rem; background:linear-gradient(180deg, rgba(255,255,255,.94) 0%, rgba(248,250,252,.94) 100%); color:#0f172a; font-size:.86rem; display:flex; align-items:center; justify-content:space-between; cursor:pointer; box-shadow:0 4px 10px rgba(15,23,42,.05); }
      .compare-dropdown-icon { color:#64748b; transition:transform .2s ease; }
      .compare-dropdown-icon.is-open { transform:rotate(180deg); }
      .compare-dropdown-menu { position:absolute; top:100%; left:0; right:0; margin-top:6px; border:1px solid rgba(203,213,225,.95); border-radius:.75rem; overflow:hidden; background:#fff; box-shadow:0 12px 26px rgba(15,23,42,.14); z-index:20; }
      .compare-dropdown-item { width:100%; border:none; background:#fff; color:#0f172a; text-align:left; padding:.52rem .62rem; font-size:.85rem; cursor:pointer; transition:background .18s ease, color .18s ease; }
      .compare-dropdown-item:hover { background:#f8fafc; }
      .compare-dropdown-item.active { background:#eef2ff; color:#312e81; font-weight:700; }
      .compare-dropdown-item:disabled { color:#94a3b8; background:#f8fafc; cursor:not-allowed; }
      .compare-suggestion-card { border:1px solid rgba(191,219,254,.9); background:linear-gradient(145deg, rgba(239,246,255,.92) 0%, rgba(255,255,255,.98) 100%); border-radius:1rem; padding:.85rem .95rem; box-shadow:0 8px 18px rgba(37,99,235,.10); }
      .compare-suggestion-title { margin:0 0 4px; color:#1e3a8a; font-size:.88rem; font-weight:800; letter-spacing:.01em; }
      .compare-suggestion-summary { margin:0 0 8px; color:#334155; font-size:.84rem; line-height:1.45; }
      .compare-suggestion-list { margin:0; padding-left:18px; color:#334155; font-size:.81rem; line-height:1.45; }
      .compare-suggestion-list li { margin-bottom:4px; }
      .compare-matrix { width:100%; min-width:760px; }
      .compare-matrix .matrix-row { grid-template-columns:minmax(260px, 1.4fr) repeat(2, minmax(210px, 1fr)); }
      .compare-header-row .matrix-cell { min-height:154px; align-items:stretch; }
      .compare-group-title { font-weight:700; }
      .pricing-footnote { margin:14px 2px 0; color:var(--muted); text-align:center; font-size:.88rem; }
      .pricing-faq-section { position:relative; z-index:10; padding:2.75rem 1.25rem 3rem; scroll-margin-top:5rem; margin-top:.5rem; border-top:1px solid rgba(226,232,240,.72); background:linear-gradient(180deg, rgba(255,255,255,.4) 0%, rgba(248,252,249,.35) 40%, transparent 100%); }
      .pricing-faq-heading { text-align:center; max-width:min(48rem, 100%); margin:0 auto 1.75rem; }
      .pricing-faq-heading .pricing-label { font-size:.8rem; letter-spacing:.1em; font-weight:600; }
      .pricing-faq-heading h2 { margin:0 0 10px; font-size:clamp(1.45rem, 2.6vw, 2rem); color:var(--text); letter-spacing:-.03em; line-height:1.2; }
      .pricing-faq-lead { margin:0; color:#64748b; font-size:.94rem; line-height:1.55; }
      .pricing-faq-shell { width:100%; max-width:100%; margin:0 auto; border-radius:1.75rem; border:1px solid rgba(187,247,208,.55); background:linear-gradient(165deg, #ffffff 0%, #f9fdfb 48%, #f4fcf7 100%); box-shadow:0 22px 48px rgba(15,23,42,.07), 0 0 0 1px rgba(255,255,255,.8) inset; overflow:hidden; }
      .pricing-faq-list { list-style:none; margin:0; padding:0; display:block; }
      .pricing-faq-item { border-bottom:1px solid rgba(226,232,240,.88); transition:background .22s ease; }
      .pricing-faq-item:last-child { border-bottom:none; }
      .pricing-faq-item.is-open { background:rgba(255,255,255,.72); }
      .pricing-faq-trigger { width:100%; border:none; background:transparent; display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; text-align:left; padding:1.2rem 1.35rem 1.15rem; cursor:pointer; font-family:inherit; transition:background .2s ease, box-shadow .2s ease; }
      .pricing-faq-trigger:hover { background:rgba(255,255,255,.55); }
      .pricing-faq-item.is-open .pricing-faq-trigger { background:linear-gradient(90deg, rgba(240,253,244,.95) 0%, rgba(255,255,255,.35) 42%, transparent 100%); box-shadow:inset 3px 0 0 0 #22c55e; }
      .pricing-faq-q { font-size:1.02rem; font-weight:600; color:#0f172a; line-height:1.45; letter-spacing:-.015em; padding-top:1px; }
      .pricing-faq-chevron-btn { flex-shrink:0; width:38px; height:38px; border-radius:999px; display:grid; place-items:center; background:rgba(248,250,252,.95); border:1px solid rgba(226,232,240,.95); color:#64748b; transition:transform .4s cubic-bezier(0.32,0.72,0,1), background .2s ease, border-color .2s ease, color .2s ease; margin-top:0; }
      .pricing-faq-trigger:hover .pricing-faq-chevron-btn { border-color:rgba(187,247,208,.9); color:#3f6b4f; }
      .pricing-faq-item.is-open .pricing-faq-chevron-btn { background:rgba(236,253,245,.98); border-color:rgba(34,197,94,.38); color:#15803d; transform:rotate(0deg); }
      .pricing-faq-panel { margin:0; display:grid; grid-template-rows:0fr; transition:grid-template-rows .48s cubic-bezier(0.32,0.72,0,1); }
      .pricing-faq-panel.is-open { grid-template-rows:1fr; }
      .pricing-faq-panel-inner { min-height:0; overflow:hidden; }
      .pricing-faq-answer { padding:0 1.35rem 1.4rem 1.35rem; padding-left:calc(1.35rem + 3px); padding-top:.35rem; color:#1e293b; font-size:.95rem; line-height:1.72; border-left:3px solid rgba(34,197,94,.4); background:linear-gradient(180deg, rgba(241,245,249,.65) 0%, rgba(255,255,255,.5) 100%); opacity:0; transform:translateY(-6px); transition:opacity .32s ease .06s, transform .42s cubic-bezier(0.32,0.72,0,1) .04s; }
      .pricing-faq-panel.is-open .pricing-faq-answer { opacity:1; transform:translateY(0); }
      .pricing-faq-panel:not(.is-open) .pricing-faq-answer { transition:opacity .22s ease, transform .32s cubic-bezier(0.4,0,1,1); }
      .pricing-faq-answer p { margin:0 0 .85rem; }
      .pricing-faq-answer p:last-child { margin-bottom:0; }
      .pricing-faq-answer a { color:#2563eb; font-weight:600; text-decoration:none; border-bottom:1px solid rgba(37,99,235,.32); transition:color .2s ease, border-color .2s ease; }
      .pricing-faq-answer a:hover { color:#4f46e5; border-bottom-color:rgba(79,70,229,.45); }
      .theme-footer { position:relative; z-index:10; margin-top:2.25rem; border-top:1px solid rgba(187,247,208,.75); background:linear-gradient(180deg, rgba(250,255,252,.92) 0%, rgba(237,252,244,.96) 100%); }
      .theme-footer-inner { max-width:82rem; margin:0 auto; padding:2rem 1.25rem 1.25rem; display:grid; grid-template-columns:1fr 1fr; grid-template-areas:"mid top" "bottom bottom"; gap:0 28px; align-items:center; }
      .theme-footer-top { grid-area:top; display:grid; grid-template-columns:1fr; gap:20px; align-items:center; padding:0 0 1.2rem; border-bottom:1px solid rgba(187,247,208,.6); justify-items:center; }
      .footer-cta-block h3 { margin:0 0 6px; color:#0f172a; font-size:1.42rem; letter-spacing:-.02em; }
      .footer-cta-block p { margin:0 0 12px; color:#4b6b56; font-size:.92rem; max-width:55ch; }
      .footer-cta-block { width:100%; max-width:520px; text-align:center; }
      .footer-email-wrap { width:100%; max-width:620px; margin:0 auto; background:linear-gradient(180deg, #f8fffb 0%, #f0fdf4 100%); border:1px solid rgba(187,247,208,.75); border-radius:999px; padding:4px; display:flex; align-items:center; }
      .footer-email-wrap input { flex:1 1 auto; border:none; background:transparent; padding:9px 16px; margin-left:0; color:#334155; outline:none; font-size:.88rem; }
      .footer-email-wrap button { width:28px; height:28px; border:none; border-radius:999px; background:linear-gradient(90deg, #2563EB 0%, #4F46E5 100%); color:#fff; display:grid; place-items:center; cursor:pointer; }
      .theme-footer-mid { grid-area:mid; display:grid; grid-template-columns:1fr; gap:22px; padding:0 0 1.2rem; border-bottom:1px solid rgba(187,247,208,.6); justify-items:center; }
      .footer-brand-head { display:flex; align-items:center; gap:8px; font-weight:700; color:#0f172a; margin-bottom:8px; }
      .footer-brand-logo { width:20px; height:20px; object-fit:contain; }
      .footer-brand-col { width:100%; max-width:420px; margin:0 auto; text-align:center; }
      .footer-brand-col p { margin:0 auto 10px; color:#4b6b56; font-size:.86rem; line-height:1.5; max-width:32ch; }
      .footer-brand-head { justify-content:center; }
      .footer-socials { display:flex; gap:8px; }
      .footer-socials { justify-content:center; }
      .footer-socials a { width:29px; height:29px; border-radius:999px; border:1px solid rgba(187,247,208,.78); color:#3f5f4b; display:grid; place-items:center; text-decoration:none; background:#f8fffb; transition:all .2s ease; }
      .footer-socials a:hover { transform:translateY(-1px); box-shadow:0 8px 14px rgba(34,197,94,.16); }
      .footer-socials a.social-instagram { color:#E1306C; border-color:rgba(225,48,108,.35); background:linear-gradient(180deg, rgba(252,231,243,.9) 0%, rgba(255,255,255,1) 100%); }
      .footer-socials a.social-facebook { color:#1877F2; border-color:rgba(24,119,242,.35); background:linear-gradient(180deg, rgba(219,234,254,.85) 0%, rgba(255,255,255,1) 100%); }
      .footer-socials a.social-linkedin { color:#0A66C2; border-color:rgba(10,102,194,.35); background:linear-gradient(180deg, rgba(219,234,254,.85) 0%, rgba(255,255,255,1) 100%); }
      .footer-socials a.social-instagram:hover { box-shadow:0 10px 16px rgba(225,48,108,.28); }
      .footer-socials a.social-facebook:hover { box-shadow:0 10px 16px rgba(24,119,242,.24); }
      .footer-socials a.social-linkedin:hover { box-shadow:0 10px 16px rgba(10,102,194,.24); }
      .theme-footer-bottom { grid-area:bottom; padding-top:1rem; display:flex; justify-content:space-between; align-items:center; gap:14px; flex-wrap:wrap; }
      .theme-footer-bottom p { margin:0; color:#6b8b74; font-size:.78rem; }
      .footer-legal-links { display:flex; gap:14px; }
      .footer-legal-links a { text-decoration:none; color:#4b6b56; font-size:.8rem; }
      @media (max-width:1280px) {
        .pricing-main { padding:5.4rem 1rem 2.4rem; }
        .pricing-container { max-width:100%; }
        .pricing-matrix { min-width:920px; }
        .matrix-row { grid-template-columns:minmax(210px, 1.16fr) repeat(6, minmax(118px, 1fr)); }
        .matrix-cell { padding:13px 10px; font-size:.86rem; }
        .matrix-cell-plan h3 { font-size:.96rem; }
        .plan-subtitle { font-size:.76rem; min-height:2em; }
        .plan-price { font-size:1.12rem; }
        .plan-cta { font-size:.74rem; padding:.48rem .62rem; width:100%; }
        .compare-matrix { min-width:700px; }
        .compare-matrix .matrix-row { grid-template-columns:minmax(220px, 1.2fr) repeat(2, minmax(170px, 1fr)); }
      }
      @media (min-width:768px) { .nav-links { display:flex; } .btn-text { display:block; } }
      @media (max-width:768px) { .nav-glass{height:58px; padding:0 12px;} .logo-text{font-size:1.06rem;} .pricing-main { padding:5rem .9rem 2rem; } .voice-rate-banner{flex-direction:column; align-items:flex-start; padding:12px 14px;} .voice-rate-banner::after{display:none;} .voice-rate-banner__price{text-align:left; padding-left:52px;} .pricing-matrix { min-width:1120px; animation:none; } .matrix-row { grid-template-columns:minmax(240px, 1.28fr) repeat(6, minmax(148px, 1fr)); } .compare-controls{justify-content:flex-start;} .compare-select{min-width:136px;} .pricing-faq-section{padding:2.2rem .9rem 2.4rem;} .pricing-faq-shell{border-radius:1.25rem;} .pricing-faq-trigger{padding:1.05rem 1rem;} .pricing-faq-q{font-size:.98rem;} .pricing-faq-answer{padding:0 1rem 1.15rem calc(1rem + 3px); font-size:.9rem;} .theme-footer-inner{padding:1.4rem .9rem 1.1rem; grid-template-columns:1fr; grid-template-areas:"top" "mid" "bottom";} .theme-footer-top{grid-template-columns:1fr; gap:14px;} .theme-footer-mid{grid-template-columns:1fr; margin-top:1rem;} .theme-footer-bottom{align-items:flex-start; flex-direction:column;} }
      @keyframes pulse { 0%,100%{opacity:.06;} 50%{opacity:.1;} }
      @keyframes rowEnter { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
      @keyframes matrixFloatLite {
        0%, 100% { transform:translateY(0); box-shadow:0 24px 40px rgba(15,23,42,.08); }
        50% { transform:translateY(-10px); box-shadow:0 32px 54px rgba(15,23,42,.14); }
      }
      @media (prefers-reduced-motion: reduce) {
        .pricing-faq-panel, .pricing-faq-answer, .pricing-faq-chevron-btn { transition:none !important; }
        .pricing-faq-answer { transform:none !important; opacity:1; }
      }
    ` }} />
  </div>
  );
};

export default VlmPricing;
